import { useMemo, useState } from 'react';
import { ModemData } from '../api/Drone';

export type DroneFilterId = 'all' | 'online' | 'arm' | 'update';
export type DroneGroupId = 'online' | 'offline';

export interface DroneGroup {
    id: DroneGroupId;
    drones: ModemData[];
}

export interface DroneFilterState {
    query: string;
    setQuery: (query: string) => void;
    filter: DroneFilterId;
    setFilter: (filter: DroneFilterId) => void;
    /** Сколько устройств попало бы в каждую чипсу при текущем поиске. */
    counts: Record<DroneFilterId, number>;
    /** Непустые группы ОНЛАЙН/ОФЛАЙН в порядке показа. */
    groups: DroneGroup[];
    /** Плоский набор видимых IMEI — его же получает карта. */
    visibleImeis: string[];
    total: number;
    visibleCount: number;
}

export const DRONE_FILTERS: readonly DroneFilterId[] = ['all', 'online', 'arm', 'update'];

/**
 * Условие то же, что у иконки обновления в ModemCard: до 2000-й версии сервер
 * не отдаёт признаки обновления, и значок там не рисуется.
 */
export function needsUpdate(drone: ModemData): boolean {
    return drone.version >= 2000 && (drone.upgradable || drone.isLastFirmware !== 0);
}

function matchesFilter(drone: ModemData, filter: DroneFilterId): boolean {
    switch (filter) {
        case 'online':
            return drone.isOnline;
        case 'arm':
            return drone.isArm;
        case 'update':
            return needsUpdate(drone);
        case 'all':
            return true;
    }
}

// numeric: «Дрон 2» встаёт перед «Дрон 10», а не после.
function byName(a: ModemData, b: ModemData): number {
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Поиск, фильтры-чипсы и группировка списка дронов. Заменяет собой
 * getDronesOrdered() из Console: тот мутировал state через .sort() на месте
 * и передавал в компаратор функцию одного аргумента — порядок был случайным.
 */
function useDroneFilter(drones: ModemData[]): DroneFilterState {
    const [query, setQuery] = useState<string>('');
    const [filter, setFilter] = useState<DroneFilterId>('all');

    const searched = useMemo<ModemData[]>(() => {
        const needle = query.trim().toLowerCase();
        if (needle === '') {
            return drones;
        }
        return drones.filter((d) =>
            d.name.toLowerCase().includes(needle) || d.imei.toLowerCase().includes(needle)
        );
    }, [drones, query]);

    const counts = useMemo<Record<DroneFilterId, number>>(() => ({
        all: searched.length,
        online: searched.filter((d) => d.isOnline).length,
        arm: searched.filter((d) => d.isArm).length,
        update: searched.filter(needsUpdate).length
    }), [searched]);

    const visible = useMemo<ModemData[]>(
        () => searched.filter((d) => matchesFilter(d, filter)),
        [searched, filter]
    );

    const groups = useMemo<DroneGroup[]>(() => {
        // .filter() уже вернул новый массив, поэтому .sort() ничего не мутирует.
        const online = visible.filter((d) => d.isOnline).sort(byName);
        const offline = visible.filter((d) => !d.isOnline).sort(byName);

        const result: DroneGroup[] = [];
        if (online.length > 0) {
            result.push({ id: 'online', drones: online });
        }
        if (offline.length > 0) {
            result.push({ id: 'offline', drones: offline });
        }
        return result;
    }, [visible]);

    const visibleImeis = useMemo<string[]>(() => visible.map((d) => d.imei), [visible]);

    return {
        query,
        setQuery,
        filter,
        setFilter,
        counts,
        groups,
        visibleImeis,
        total: drones.length,
        visibleCount: visible.length
    };
}

export default useDroneFilter;
